import { on } from '@ember/modifier';
import { service } from '@ember/service';
import Component from '@glimmer/component';

import clipboard from 'ember-cli-clipboard/modifiers/clipboard';
import { t } from 'ember-intl';
import { resource } from 'ember-resources';
import { not, or } from 'ember-truth-helpers';
import { trackedFunction } from 'reactiveweb/function';

import Button from 'umg-ember-backend/components/ui/button';
import Form from 'umg-ember-backend/components/ui/form';
import InfoBox from 'umg-ember-backend/components/ui/info-box';
import { query } from 'umg-ember-backend/data/builders/umg-rest';
import { getBem } from 'umg-ember-backend/utils/get-bem';

import AssetInformation from '../asset-information';
import styles from './styles.module.css';

const bem = getBem(styles);

function rollbackDownloadLink(assetDownloadLink, recordRelationships) {
  if (
    !assetDownloadLink ||
    assetDownloadLink.isDestroying ||
    assetDownloadLink.isDestroyed
  ) {
    return;
  }

  assetDownloadLink.rollbackAttributes();
  recordRelationships.rollback(assetDownloadLink);
}

export default class AssetToolsDownloadComponent extends Component {
  // Services
  @service flashMessages;
  @service recordRelationships;
  @service store;

  // Getter and setter
  get assetDownloadLink() {
    return this.downloadState.value;
  }

  get assetFormatRelation() {
    const { asset } = this.args;
    const assetFormatRelations = asset.resolvedAssetFormatRelations.value || [];

    return assetFormatRelations.find((assetFormatRelation) => {
      const assetDownloadLinkId = this.assetDownloadLink.format?.get('id');

      return assetFormatRelation.get('assetFormat.id') === assetDownloadLinkId;
    });
  }

  get isAssetFormatRendered() {
    return this.assetFormatRelation?.get('status') === 'success';
  }

  get downloadFormats() {
    const downloadFormats = this.downloadFormatsState.value ?? [];

    if (this.args.asset.isVideo) {
      return downloadFormats;
    }

    return downloadFormats.filter((downloadFormat) => {
      return downloadFormat.id === '195';
    });
  }

  // Functions
  createAssetFormatRelation = async () => {
    try {
      const assetFormat = await this.assetDownloadLink.format;

      await this.store
        .createRecord('asset-format-relation', {
          asset: this.args.asset,
          assetFormat,
          priority: 1,
          width: assetFormat.width,
          height: assetFormat.height,
        })
        .save();

      await this.args.asset.save();
    } catch (error) {
      this.flashMessages.serverError(error);
    }
  };

  directDownload = async () => {
    try {
      const format = await this.assetDownloadLink.format;
      const downloadLink = this.store.createRecord('asset-download-link', {
        maxdownloads: 1,
        hoursvalid: 1,
        asset: this.args.asset,
        format,
      });
      await downloadLink.save();

      window.open(downloadLink.accessLinks.de, '_blank');
    } catch (error) {
      this.flashMessages.serverError(error);
    }
  };

  generateAccessKey = async () => {
    const assetDownloadLink = this.assetDownloadLink;
    const flashMessages = this.flashMessages;

    try {
      await assetDownloadLink.save();
    } catch (error) {
      flashMessages.serverError(error);
    }
  };

  downloadFormatsState = trackedFunction(this, async () => {
    try {
      const { content } = await this.store.request(
        query('asset-format', {
          'filter[flagIsDownladFormat]': true,
        }),
      );

      return content.data ?? [];
    } catch (error) {
      this.flashMessages.serverError(error);

      return [];
    }
  });

  downloadState = resource(this, ({ on, use }) => {
    const asset = this.args.asset;
    const downloadFormatsState = this.downloadFormatsState;
    const isVideo = asset.isVideo;
    const recordRelationships = this.recordRelationships;
    const store = this.store;
    let assetDownloadLink;

    const state = use(
      trackedFunction(async () => {
        const downloadFormats = await downloadFormatsState.promise;

        if (!downloadFormats.length) {
          return null;
        }

        const sourceFormat = downloadFormats.find((downloadFormat) => {
          return downloadFormat.id === '195';
        });
        const videoFormat = downloadFormats.find((downloadFormat) => {
          return downloadFormat.id === '836';
        });
        const format = isVideo ? videoFormat : sourceFormat;

        if (!format) {
          return null;
        }

        assetDownloadLink = store.createRecord('asset-download-link', {
          maxdownloads: 3,
          hoursvalid: 24,
          asset,
          format,
        });

        return assetDownloadLink;
      }),
    );

    on.cleanup(() => {
      rollbackDownloadLink(assetDownloadLink, recordRelationships);
    });

    return state.current;
  });

  // Template
  <template>
    <div class={{(bem)}} ...attributes>
      {{#if this.assetDownloadLink.accesskey}}
        <AssetInformation
          @asset={{@asset}}
          @copySuccess={{@copySuccess}}
          @copyError={{@copyError}}
        />

        <label for='de-link'>
          {{t 'asset_tools.download.results.access_link_de'}}
        </label>
        <div class={{bem 'result'}}>
          <input
            value={{this.assetDownloadLink.accessLinks.de}}
            class={{bem 'input'}}
            id='de-link'
            data-test-asset-tool-download-input-de
            readonly
          />

          <Button
            @label={{t 'asset_tools.download.copy'}}
            class={{bem 'copy'}}
            data-test-asset-tool-download-copy-button
            {{clipboard
              text=this.assetDownloadLink.accessLinks.de
              onSuccess=@copySuccess
              onError=@copyError
            }}
          />

          <a
            href={{this.assetDownloadLink.accessLinks.de}}
            target='_blank'
            rel='noopener noreferrer'
            class='c-ui-button c-ui-button--style-primary'
          >
            {{t 'asset_tools.download.open_link'}}
          </a>
        </div>

        <label for='en-link'>
          {{t 'asset_tools.download.results.access_link_en'}}
        </label>
        <div class={{bem 'result'}}>
          <input
            value={{this.assetDownloadLink.accessLinks.en}}
            class={{bem 'input'}}
            id='en-link'
            data-test-asset-tool-download-input-en
            readonly
          />

          <Button
            @label={{t 'asset_tools.download.copy'}}
            class={{bem 'copy'}}
            ata-test-asset-tool-download-copy-button
            {{clipboard
              text=this.assetDownloadLink.accessLinks.en
              onSuccess=@copySuccess
              onError=@copyError
            }}
          />
          <a
            href={{this.assetDownloadLink.accessLinks.en}}
            target='_blank'
            rel='noopener noreferrer'
            class='c-ui-button c-ui-button--style-primary'
          >
            {{t 'asset_tools.download.open_link'}}
          </a>
        </div>
      {{else if this.assetDownloadLink}}
        <Form @model={{this.assetDownloadLink}} as |form|>
          <form.field
            @property='format'
            @type='select-record'
            @options={{this.downloadFormats}}
          />

          {{#unless this.isAssetFormatRendered}}
            <InfoBox @type='warning'>
              {{t 'asset_tools.download.missing_format'}}

              {{#unless this.assetFormatRelation}}
                <br /><br />
                <Button
                  @label={{t 'asset_tools.download.create_format_relation'}}
                  data-test-asset-tool-create-format-relation-button
                  {{on 'click' this.createAssetFormatRelation}}
                />
              {{/unless}}
            </InfoBox>
          {{/unless}}

          <form.field @property='hoursvalid' @type='number' />
          <form.field @property='maxdownloads' @type='number' />
        </Form>

        <Button
          @disabled={{or
            this.assetDownloadLink.validations.isInvalid
            (not this.isAssetFormatRendered)
          }}
          @label={{t 'asset_tools.download.generate'}}
          data-test-asset-tool-generate-download-link-button
          {{on 'click' this.generateAccessKey}}
        />

        <Button
          @disabled={{not this.isAssetFormatRendered}}
          @label={{t 'asset_tools.download.direct_download'}}
          data-test-asset-tool-direct-download-button
          {{on 'click' this.directDownload}}
        />
      {{/if}}
    </div>
  </template>
}
